import React, { FC, useEffect, useRef } from 'react';
import type { OrganisationMember } from '@dreamai/types';
import './delete-account-modal.scss';

interface OrganisationRemoveMemberModalProps {
  member: OrganisationMember | null;
  isSubmitting: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (member: OrganisationMember) => void;
}

const FOCUSABLE =
  'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * "Are you sure?" before a member is removed from the organisation, which
 * permanently deletes their account and Personal Assistant after a
 * 5-minute grace period.
 * Focus starts on "Cancel", Tab cycles inside the dialog, Escape closes it
 * and focus returns to whatever opened it.
 */
const OrganisationRemoveMemberModal: FC<OrganisationRemoveMemberModalProps> = ({
  member,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const dismissRef = useRef<HTMLButtonElement>(null);
  const latest = useRef({ isSubmitting, onClose });
  latest.current = { isSubmitting, onClose };
  const open = !!member;

  useEffect(() => {
    if (!open) return;
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    dismissRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!latest.current.isSubmitting) latest.current.onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (
        e.shiftKey &&
        (active === first || !dialogRef.current.contains(active))
      ) {
        e.preventDefault();
        last.focus();
      } else if (
        !e.shiftKey &&
        (active === last || !dialogRef.current.contains(active))
      ) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus();
    };
  }, [open]);

  if (!member) return null;

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <div
      className='delete-account-modal-overlay'
      onClick={handleClose}
      data-testid='organisation-remove-member-modal'
    >
      <div
        ref={dialogRef}
        className='delete-account-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='organisation-remove-member-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='organisation-remove-member-title'>
            Remove {member.name}
          </p>
          <button
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={isSubmitting}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          <h3 className='body-title'>Are you sure?</h3>
          <div className='body-text'>
            <p>
              {member.name}&rsquo;s AI Secretary and Dream account will be
              permanently deleted, including their messages, files, memory,
              Dream email address and connected apps.
            </p>
            <p>
              You have 5 minutes to restore them from this list. After that the
              deletion can&rsquo;t be undone, and their seat comes off your bill
              from the next payment.
            </p>
          </div>
        </div>

        <div className='modal-footer'>
          {error && <p className='delete-error'>{error}</p>}
          <button
            ref={dismissRef}
            className='btn-delete-dismiss'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className='btn-delete-confirm'
            onClick={() => onConfirm(member)}
            disabled={isSubmitting}
            data-testid='organisation-remove-member-confirm'
          >
            {isSubmitting ? 'Removing…' : `Remove ${member.name}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganisationRemoveMemberModal;
